import { getLocalTimeZone, today } from '@internationalized/date'
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import {
  type CalendarProps as CalendarPropsRac,
  type DateValue,
  type Key,
  type RangeCalendarProps as RangeCalendarPropsRac,
  Button,
  CalendarCell as CalendarCellRac,
  CalendarGridBody as CalendarGridBodyRac,
  CalendarGridHeader as CalendarGridHeaderRac,
  CalendarGrid as CalendarGridRac,
  CalendarHeaderCell as CalendarHeaderCellRac,
  CalendarMonthPicker,
  Calendar as CalendarRac,
  CalendarYearPicker,
  composeRenderProps,
  ListBox,
  ListBoxItem,
  Popover,
  RangeCalendar as RangeCalendarRac,
  Select,
  SelectValue,
} from 'react-aria-components'

import { cn } from '@/infra'

const VISIBLE_YEARS_PADRAO = 100

interface BaseCalendarProps {
  className?: string
  visibleYears?: number
}

type CalendarProps = CalendarPropsRac<DateValue> & BaseCalendarProps
type RangeCalendarProps = RangeCalendarPropsRac<DateValue> & BaseCalendarProps

type SeletorNavegacaoProps = {
  'aria-label': string
  value: Key
  onChange: (key: Key | null) => void
  items: Array<{ id: number; formatted: string }>
}

function SeletorNavegacao({ items, value, onChange, ...props }: Readonly<SeletorNavegacaoProps>) {
  return (
    <Select {...props} selectedKey={value} onSelectionChange={onChange} className="min-w-0">
      <Button className="hover:bg-accent hover:text-primary focus-visible:ring-ring/50 flex h-9 max-w-full items-center gap-1 rounded-md px-2 text-sm font-medium transition-[color,box-shadow] outline-none focus-visible:ring-[3px]">
        <SelectValue className="truncate first-letter:uppercase" />
        <ChevronDownIcon size={14} className="shrink-0 opacity-60" />
      </Button>
      <Popover className="bg-popover text-popover-foreground data-entering:animate-in data-exiting:animate-out data-[entering]:fade-in-0 data-[exiting]:fade-out-0 z-50 max-h-60 w-(--trigger-width) min-w-32 overflow-auto rounded-md border shadow-md">
        <ListBox items={items} className="p-1 outline-none">
          {(item) => (
            <ListBoxItem
              id={item.id}
              className="data-focused:bg-accent data-focused:text-accent-foreground flex cursor-default items-center rounded-sm px-2 py-1.5 text-sm outline-none select-none first-letter:uppercase data-selected:font-medium"
            >
              {item.formatted}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </Select>
  )
}

function CalendarHeader({ visibleYears = VISIBLE_YEARS_PADRAO }: Readonly<{ visibleYears?: number }>) {
  return (
    <header className="flex w-full items-center gap-1 pb-1">
      <Button
        slot="previous"
        className="text-muted-foreground/80 hover:bg-accent hover:text-primary focus-visible:ring-ring/50 flex size-9 shrink-0 items-center justify-center rounded-md transition-[color,box-shadow] outline-none focus-visible:ring-[3px]"
      >
        <ChevronLeftIcon size={16} />
      </Button>
      <div className="flex grow items-center justify-center gap-1">
        <CalendarMonthPicker format="long">
          {(pickerProps) => <SeletorNavegacao {...pickerProps} />}
        </CalendarMonthPicker>
        <CalendarYearPicker visibleYears={visibleYears}>
          {(pickerProps) => <SeletorNavegacao {...pickerProps} />}
        </CalendarYearPicker>
      </div>
      <Button
        slot="next"
        className="text-muted-foreground/80 hover:bg-accent hover:text-primary focus-visible:ring-ring/50 flex size-9 shrink-0 items-center justify-center rounded-md transition-[color,box-shadow] outline-none focus-visible:ring-[3px]"
      >
        <ChevronRightIcon size={16} />
      </Button>
    </header>
  )
}

function CalendarGridComponent({ isRange = false }: Readonly<{ isRange?: boolean }>) {
  const now = today(getLocalTimeZone())

  return (
    <CalendarGridRac className="mx-auto border-spacing-0">
      <CalendarGridHeaderRac>
        {(day) => (
          <CalendarHeaderCellRac className="text-muted-foreground/80 size-9 rounded-md p-0 text-xs font-medium">
            {day}
          </CalendarHeaderCellRac>
        )}
      </CalendarGridHeaderRac>
      <CalendarGridBodyRac className="[&_td]:px-0 [&_td]:py-px">
        {(date) => (
          <CalendarCellRac
            date={date}
            className={cn(
              'data-selected:bg-primary data-selected:text-primary-foreground data-focus-visible:ring-ring/50 relative flex size-9 items-center justify-center rounded-md p-0 text-sm font-normal whitespace-nowrap [transition-property:color,background-color,border-radius,box-shadow] duration-150 outline-none data-disabled:pointer-events-none data-disabled:opacity-30 data-focus-visible:z-10 data-focus-visible:ring-[3px] data-hovered:bg-sky-50 data-hovered:text-sky-800 data-unavailable:pointer-events-none data-unavailable:line-through data-unavailable:opacity-30',
              // Range-specific styles
              isRange &&
                'data-selected:bg-accent data-selected:text-foreground data-invalid:data-selection-end:bg-destructive data-invalid:data-selection-start:bg-destructive data-selection-end:bg-primary data-selection-start:bg-primary data-selection-end:text-primary-foreground data-selection-start:text-primary-foreground data-invalid:bg-red-100 data-selected:rounded-none data-selection-end:rounded-e-md data-invalid:data-selection-end:text-white data-selection-start:rounded-s-md data-invalid:data-selection-start:text-white',
              // Today indicator styles
              date.compare(now) === 0 &&
                cn(
                  'after:bg-primary after:pointer-events-none after:absolute after:start-1/2 after:bottom-1 after:z-10 after:size-[3px] after:-translate-x-1/2 after:rounded-full',
                  isRange
                    ? 'data-selection-end:after:bg-background data-selection-start:after:bg-background'
                    : 'data-selected:after:bg-background',
                ),
            )}
          />
        )}
      </CalendarGridBodyRac>
    </CalendarGridRac>
  )
}

function Calendar({ className, visibleYears, ...props }: CalendarProps) {
  return (
    <CalendarRac
      {...props}
      weeksInMonth={6}
      className={composeRenderProps(className, (_className) => cn('w-72', _className))}
    >
      <CalendarHeader visibleYears={visibleYears} />
      <CalendarGridComponent />
    </CalendarRac>
  )
}

function RangeCalendar({ className, visibleYears, ...props }: RangeCalendarProps) {
  return (
    <RangeCalendarRac
      {...props}
      weeksInMonth={6}
      className={composeRenderProps(className, (_className) => cn('w-72', _className))}
    >
      <CalendarHeader visibleYears={visibleYears} />
      <CalendarGridComponent isRange />
    </RangeCalendarRac>
  )
}

export { Calendar, RangeCalendar }
